import {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { printPlugin } from "@react-pdf-viewer/print";
import { fullScreenPlugin } from "@react-pdf-viewer/full-screen";
import { toolbarPlugin } from "@react-pdf-viewer/toolbar";

// Import styles
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/zoom/lib/styles/index.css";
import "@react-pdf-viewer/page-navigation/lib/styles/index.css";
import "@react-pdf-viewer/print/lib/styles/index.css";
import "@react-pdf-viewer/full-screen/lib/styles/index.css";
import "@react-pdf-viewer/toolbar/lib/styles/index.css";

import workerSrc from "pdfjs-dist/build/pdf.worker.min.js";

/**
 * Enhanced PDF Viewer component with zoom, pan, print, and navigation controls
 * Uses react-pdf-viewer library for rendering while exposing controls to parent
 *
 * @param {string} fileUrl - URL of the PDF file to display
 * @param {number} currentPage - Current page number (1-indexed, controlled from parent)
 * @param {function} onPageChange - Callback when page changes (receives 1-indexed page number)
 * @param {number} zoom - Zoom level as percentage (e.g., 100 = 100%)
 * @param {function} onZoomChange - Callback when zoom changes (receives percentage)
 * @param {number} rotation - Rotation angle in degrees
 * @param {function} onDocumentLoad - Callback when document loads (receives { totalPages, pageWidth, pageHeight })
 * @param {function} onPageNavigationComponents - Callback to receive page navigation components (GoToPreviousPage, GoToNextPage, CurrentPageLabel, NumberOfPages)
 * @param {object} style - Additional styles for the container
 */
const PdfViewer = forwardRef(
  (
    {
      fileUrl,
      currentPage = 1,
      onPageChange,
      zoom = 100,
      onZoomChange,
      rotation = 0,
      onDocumentLoad,
      onPageNavigationComponents,
      style = {},
    },
    ref
  ) => {
    const containerRef = useRef(null);
    const [totalPages, setTotalPages] = useState(0);
    const [pageWidth, setPageWidth] = useState(0);
    const [pageHeight, setPageHeight] = useState(0);
    const [internalZoom, setInternalZoom] = useState(zoom / 100);
    const zoomPluginRef = useRef(null);
    const pageNavPluginRef = useRef(null);
    const printPluginRef = useRef(null);
    const toolbarPluginRef = useRef(null);

    // Initialize zoom plugin with controlled zoom
    const zoomPluginInstance = zoomPlugin({
      onZoom: (zoomLevel) => {
        const zoomPercent = Math.round(zoomLevel * 100);
        setInternalZoom(zoomLevel);
        if (onZoomChange) {
          onZoomChange(zoomPercent);
        }
      },
    });
    zoomPluginRef.current = zoomPluginInstance;
    const { zoomTo } = zoomPluginInstance;

    // Initialize page navigation plugin
    const pageNavigationPluginInstance = pageNavigationPlugin();
    pageNavPluginRef.current = pageNavigationPluginInstance;
    const {
      jumpToPage,
      GoToPreviousPage,
      GoToNextPage,
      CurrentPageLabel,
      NumberOfPages,
    } = pageNavigationPluginInstance;

    // Expose page navigation components to parent
    useEffect(() => {
      if (onPageNavigationComponents && GoToPreviousPage && GoToNextPage) {
        onPageNavigationComponents({
          GoToPreviousPage,
          GoToNextPage,
          CurrentPageLabel,
          NumberOfPages,
        });
      }
    }, [
      onPageNavigationComponents,
      GoToPreviousPage,
      GoToNextPage,
      CurrentPageLabel,
      NumberOfPages,
    ]);

    // Initialize print plugin
    const printPluginInstance = printPlugin();
    printPluginRef.current = printPluginInstance;
    const { print } = printPluginInstance;

    // Initialize full screen plugin
    const fullScreenPluginInstance = fullScreenPlugin();

    // Initialize toolbar plugin - connects to all other plugins automatically
    const toolbarPluginInstance = toolbarPlugin({
      // Toolbar will automatically use zoomPluginInstance, pageNavigationPluginInstance,
      // printPluginInstance, and fullScreenPluginInstance from the plugins array
    });
    toolbarPluginRef.current = toolbarPluginInstance;
    const { Toolbar } = toolbarPluginInstance;

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      zoomTo: (zoomPercent) => {
        if (zoomPluginRef.current && zoomPluginRef.current.zoomTo) {
          zoomPluginRef.current.zoomTo(zoomPercent / 100);
        }
      },
      jumpToPage: (pageNumber) => {
        if (pageNavPluginRef.current && pageNavPluginRef.current.jumpToPage) {
          pageNavPluginRef.current.jumpToPage(pageNumber - 1); // Convert to 0-indexed
        }
      },
      print: () => {
        if (printPluginRef.current && printPluginRef.current.print) {
          printPluginRef.current.print();
        }
      },
      getTotalPages: () => totalPages,
      getPageDimensions: () => ({ width: pageWidth, height: pageHeight }),
      getContainer: () => containerRef.current,
      getPageNavigationComponents: () => ({
        GoToPreviousPage,
        GoToNextPage,
        CurrentPageLabel,
        NumberOfPages,
      }),
    }));

    // Handle document load
    const handleDocumentLoad = async (e) => {
      try {
        const numPages = e.doc.numPages;
        setTotalPages(numPages);

        // Get dimensions from first page
        const firstPage = await e.doc.getPage(0);
        const viewport = firstPage.getViewport({ scale: 1.0 });
        setPageWidth(viewport.width);
        setPageHeight(viewport.height);

        if (onDocumentLoad) {
          onDocumentLoad({
            totalPages: numPages,
            pageWidth: viewport.width,
            pageHeight: viewport.height,
          });
        }
      } catch (error) {
        console.error("Error loading PDF document:", error);
      }
    };

    // Handle page change
    const handlePageChange = (e) => {
      if (onPageChange) {
        onPageChange(e.currentPage + 1); // Convert to 1-indexed
      }
    };

    // Sync current page with plugin when prop changes
    useEffect(() => {
      if (
        pageNavPluginRef.current &&
        pageNavPluginRef.current.jumpToPage &&
        currentPage > 0 &&
        totalPages > 0 &&
        currentPage <= totalPages
      ) {
        pageNavPluginRef.current.jumpToPage(currentPage - 1); // Convert to 0-indexed
      }
    }, [currentPage, totalPages]);

    // Sync zoom with plugin when prop changes
    useEffect(() => {
      const zoomScale = zoom / 100;
      if (
        zoomPluginRef.current &&
        zoomPluginRef.current.zoomTo &&
        Math.abs(zoomScale - internalZoom) > 0.01
      ) {
        zoomPluginRef.current.zoomTo(zoomScale);
      }
    }, [zoom, internalZoom]);

    if (!fileUrl) return null;

    return (
      <div
        ref={containerRef}
        style={{
          height: "100%",
          width: "100%",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          ...style,
        }}
      >
        {/* Custom styles for toolbar to match CompactViewerHeader */}
        <style>{`
          .pdf-viewer-toolbar-wrapper {
            height: 40px !important;
            min-height: 40px !important;
            border-bottom: 1px solid #E5E7EB !important;
            background-color: #F2F2F2 !important;
            padding: 0 4px !important;
          }
          .pdf-viewer-toolbar-wrapper .rpv-core__toolbar {
            height: 40px !important;
            min-height: 40px !important;
            background-color: transparent !important;
            border: none !important;
          }
          .pdf-viewer-toolbar-wrapper .rpv-core__toolbar-body {
            height: 40px !important;
            min-height: 40px !important;
            background-color: transparent !important;
          }
          .pdf-viewer-toolbar-wrapper button {
            padding: 4px !important;
            min-width: 32px !important;
            height: 32px !important;
            color: #4A5565 !important;
          }
          .pdf-viewer-toolbar-wrapper button:hover {
            background-color: #E5E7EB !important;
          }
          .pdf-viewer-toolbar-wrapper button:disabled {
            color: #E5E7EB !important;
          }
          .pdf-viewer-toolbar-wrapper input {
            font-family: Roboto, sans-serif !important;
            font-size: 13px !important;
            color: #4A5565 !important;
          }
          .pdf-viewer-toolbar-wrapper .rpv-core__display--block {
            font-family: Roboto, sans-serif !important;
            font-size: 13px !important;
            color: #4A5565 !important;
          }
        `}</style>
        <Worker workerUrl={workerSrc}>
          {/* Default PDF Viewer Toolbar - Styled to match CompactViewerHeader */}
          <div className="pdf-viewer-toolbar-wrapper">
            <Toolbar />
          </div>

          {/* PDF Viewer Content */}
          <div
            style={{
              flex: 1,
              height: "100%",
              width: "100%",
              position: "relative",
              overflow: "hidden",
              transform: `rotate(${rotation}deg)`,
              transformOrigin: "center center",
              transition: "transform 0.2s ease-in-out",
            }}
          >
            <Viewer
              fileUrl={fileUrl}
              plugins={[
                toolbarPluginInstance,
                zoomPluginInstance,
                pageNavigationPluginInstance,
                printPluginInstance,
                fullScreenPluginInstance,
              ]}
              onDocumentLoad={handleDocumentLoad}
              onPageChange={handlePageChange}
              defaultScale={zoom / 100}
              initialPage={currentPage - 1} // 0-indexed
            />
          </div>
        </Worker>
      </div>
    );
  }
);

PdfViewer.displayName = "PdfViewer";

export default PdfViewer;
